import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { groupByDay } from '../../lib/dates.js'
import { flattenPages } from '../../lib/queries/conversation-cache.js'
import { useConversationList, useDeleteConversation, useRenameConversation } from '../../lib/queries/conversations.js'
import { useSystems } from '../../lib/queries/systems.js'
import { Icon } from '../icons/Icon.jsx'
import { Menu } from '../ui/Menu.jsx'
import { RenameDialog } from '../ui/RenameDialog.jsx'
import { UserMenu } from '../ui/UserMenu.jsx'
import styles from './Sidebar.module.css'

function useDebounced(value, delay = 250) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

export function Sidebar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const { data: systems = [] } = useSystems()
  const navigate = useNavigate()
  const rename = useRenameConversation()
  const remove = useDeleteConversation()
  const [renaming, setRenaming] = useState(null) // the conversation being renamed
  const [search, setSearch] = useState('')
  const q = useDebounced(search.trim())

  const filters = useMemo(() => (q ? { q } : {}), [q])

  const list = useConversationList(filters)
  const items = flattenPages(list.data)
  const groups = groupByDay(items, (c) => c.lastMessageAt)

  const activeId = pathname.startsWith('/c/') ? pathname.slice(3) : null

  // Load the next page when the end of the list scrolls into view.
  const sentinel = useRef(null)
  useEffect(() => {
    const el = sentinel.current
    if (!el || !list.hasNextPage) return
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !list.isFetchingNextPage) list.fetchNextPage()
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [list.hasNextPage, list.isFetchingNextPage, list.fetchNextPage])

  return (
    <aside className={styles.sidebar}>
      <Link to="/" className={styles.brand} title="Back to DCKAP Hub">
        <span className={styles.mark}>
          <Icon name="logo" size={18} strokeWidth={1.7} />
        </span>
        <span className={styles.brandText}>
          <span className={styles.brandName}>Knowledge Base</span>
          <span className={styles.brandSub}>DCKAP Hub</span>
        </span>
      </Link>

      <Link
        to="/new"
        className={`${styles.newQuestion} ${pathname === '/new' ? styles.newQuestionActive : ''}`}
        aria-current={pathname === '/new' ? 'page' : undefined}
      >
        <span className={styles.newQuestionLabel}>
          <Icon name="plus" size={16} strokeWidth={1.7} />
          New question
        </span>
        <span className={styles.kbdDark}>{isMac ? '⌘ K' : 'Ctrl K'}</span>
      </Link>

      <label className={styles.search}>
        <Icon name="search" size={15} strokeWidth={1.6} />
        <span className="sr-only">Search past questions</span>
        <input
          type="search"
          placeholder="Search past questions"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </label>

      <nav aria-label="Question history" className={styles.history}>
        {list.isPending ? (
          <HistorySkeleton />
        ) : list.isError ? (
          <p className={styles.empty}>
            Couldn't load your questions.{' '}
            <button type="button" className={styles.linkButton} onClick={() => list.refetch()}>
              Retry
            </button>
          </p>
        ) : items.length === 0 ? (
          <p className={styles.empty}>
            {q ? 'No questions match.' : 'Your questions will show up here.'}
          </p>
        ) : (
          groups.map((group) => (
            <div key={group.label} className={styles.group}>
              <div className={`section-label ${styles.groupLabel}`}>{group.label}</div>
              {group.items.map((conversation) => {
                const active = conversation.id === activeId
                return (
                  <div key={conversation.id} className={`${styles.item} ${active ? styles.itemActive : ''}`}>
                    <Link
                      to="/c/$conversationId"
                      params={{ conversationId: conversation.id }}
                      className={styles.itemLink}
                      aria-current={active ? 'page' : undefined}
                    >
                      <span className={styles.itemTitle}>{conversation.title}</span>
                    </Link>
                    <Menu
                      trigger={(props) => (
                        <button
                          type="button"
                          className={`icon-btn ${styles.itemMenu}`}
                          aria-label={`Options for ${conversation.title}`}
                          {...props}
                        >
                          <Icon name="dots" size={14} />
                        </button>
                      )}
                      items={[
                        { label: 'Rename', onSelect: () => setRenaming(conversation) },
                        {
                          label: 'Delete',
                          danger: true,
                          onSelect: () => {
                            remove.mutate({ id: conversation.id, title: conversation.title })
                            if (active) navigate({ to: '/new' })
                          },
                        },
                      ]}
                    />
                  </div>
                )
              })}
            </div>
          ))
        )}
        <div ref={sentinel} aria-hidden="true" />
        {list.isFetchingNextPage ? <HistorySkeleton rows={2} /> : null}
      </nav>

      <div className={styles.footer}>
        <Link
          to="/library"
          className={`${styles.footerLink} ${pathname.startsWith('/library') ? styles.footerLinkActive : ''}`}
          aria-current={pathname.startsWith('/library') ? 'page' : undefined}
        >
          <Icon name="layers" size={16} strokeWidth={1.6} />
          System library
          <span className={styles.footerMeta}>
            {systems.length} {systems.length === 1 ? 'system' : 'systems'}
          </span>
        </Link>
        <Link
          to="/settings"
          className={`${styles.footerLink} ${pathname.startsWith('/settings') ? styles.footerLinkActive : ''}`}
          aria-current={pathname.startsWith('/settings') ? 'page' : undefined}
        >
          <Icon name="sliders" size={16} strokeWidth={1.6} />
          Settings
        </Link>
        <div className={styles.user}>
          <UserMenu />
        </div>
      </div>
      <RenameDialog
        open={Boolean(renaming)}
        initialTitle={renaming?.title ?? ''}
        onClose={() => setRenaming(null)}
        onSave={(title) => {
          if (title && title !== renaming.title) rename.mutate({ id: renaming.id, title })
          setRenaming(null)
        }}
      />
    </aside>
  )
}

function HistorySkeleton({ rows = 5 }) {
  return (
    <div className={styles.group} aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className={styles.skeletonItem}>
          <div className="skeleton" style={{ height: 12, width: `${70 - i * 7}%` }} />
          <div className="skeleton" style={{ height: 10, width: '40%' }} />
        </div>
      ))}
    </div>
  )
}
